import type { LayerKey } from '@festina-life/api'
import { assertNotEmpty } from '@festina-life/core'
import {
  PathBreadcrumb,
  type PathPart,
  Typography,
} from '@festina-life/ui/structure'
import { useMemo } from 'react'

import { useTemplateIsLibrary } from '../../hooks/useTemplateIsLibrary'
import { configurationItem } from '../../system/paths'
import { useNavigationParams } from '../../system/useNavigationParams'
import { TemplatePathParts } from '../../uiModels/templateOperations/helpers'
import { ConfigurationItemIcon } from '../configurationItemIcon/ConfigurationItemIcon'
import { buildPartialTemplatePathParts } from './buildPartialTemplatePathParts'

type Props = {
  value: string
  shouldRenderTemplateId?: boolean
  className?: string
  variant?: 'normal' | 'contrast'
  layer: LayerKey
  block: string
  isInteractive: boolean
}

export const TemplatePath = ({
  value,
  className,
  shouldRenderTemplateId,
  variant = 'normal',
  isInteractive,
  layer,
  block,
}: Props) => {
  const templateId = TemplatePathParts.exec(value)?.groups?.['templateId']
  assertNotEmpty(templateId)

  const remainingPath =
    TemplatePathParts.exec(value)?.groups?.['intermediatePath']
  const checkIsLibrary = useTemplateIsLibrary()

  const isLibrary = useMemo(
    () => checkIsLibrary(templateId),
    [checkIsLibrary, templateId],
  )

  const { server, branch, workspaceName } = useNavigationParams()

  // Shared label for template ID part
  const templateIdLabel = useMemo(
    () => (
      <div className="flex items-center gap-x-2">
        <ConfigurationItemIcon type={isLibrary ? 'library' : 'template'} />
        <Typography>{templateId}</Typography>
      </div>
    ),
    [isLibrary, templateId],
  )

  const pathParts = useMemo(() => {
    const parts: PathPart[] = []

    // Add template ID part if requested
    if (shouldRenderTemplateId) {
      parts.push({
        label: templateIdLabel,
        ...(isInteractive
          ? {
              href: configurationItem({
                server,
                block,
                layer,
                branch,
                workspaceName,
                itemId: templateId,
                type: isLibrary ? 'library' : 'template',
              }),
            }
          : {}),
      })
    }

    // Add remaining path parts if they exist
    if (remainingPath) {
      const partialParts = buildPartialTemplatePathParts({
        value: remainingPath,
        templateId,
        server,
        block,
        layer,
        branch,
        workspaceName,
        itemType: isLibrary ? 'library' : 'template',
        currentPath: `{${templateId}}`,
        isInteractive,
      })
      parts.push(...partialParts)
    }

    return parts
  }, [
    block,
    isInteractive,
    shouldRenderTemplateId,
    templateIdLabel,
    isLibrary,
    templateId,
    remainingPath,
    server,
    layer,
    branch,
    workspaceName,
  ])

  return (
    <PathBreadcrumb parts={pathParts} variant={variant} className={className} />
  )
}
